import { useEffect, useMemo, useState } from 'react';
import { Plus, Wallet, Users, Pencil, Trash2, UserPlus, Building2, Cpu } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { BudgetBar } from '../components/BudgetBar';
import { CardSkeletonGrid, EmptyState } from '../components/ui/States';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { Field, Input, Select } from '../components/ui/Field';
import { budgetService, BudgetError } from '../services/budgetService';
import { modelsService } from '../services/modelsService';
import { usageService, type UsageBucket } from '../services/usageService';
import { toast } from '../store/uiStore';
import { useModelsStore } from '../store/modelsStore';
import { formatBRL, uid, nowIso, cn } from '../lib/utils';
import type { Area, Person, Model } from '../types';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function BudgetPage() {
  const canManage = useAgentStudioPermissions().has(P.budgetManage);
  const [areas, setAreas] = useState<Area[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [spentByArea, setSpentByArea] = useState<Record<string, number>>({});
  const [spentByPerson, setSpentByPerson] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const [areaForm, setAreaForm] = useState<Area | null>(null);
  const [personForm, setPersonForm] = useState<Person | null>(null);
  const [deleteArea, setDeleteArea] = useState<Area | null>(null);
  const [deletePerson, setDeletePerson] = useState<Person | null>(null);

  // Governança de modelos (allowlists por área/pessoa).
  const catalog = useModelsStore((s) => s.catalog);
  const ensureModels = useModelsStore((s) => s.load);
  const reloadAllowed = useModelsStore((s) => s.reloadAllowed);
  const [areaModelIds, setAreaModelIds] = useState<string[]>([]);
  const [personModelIds, setPersonModelIds] = useState<string[]>([]);
  // Universo disponível para a pessoa = modelos liberados na área dela (Pessoa ⊆ Área).
  const [personAvailableIds, setPersonAvailableIds] = useState<string[]>([]);

  async function load() {
    const [a, p, summary] = await Promise.all([
      budgetService.listAreas(),
      budgetService.listPeople(),
      usageService.summary().catch(() => null),
    ]);
    // Gasto real do mês (ledger), por área e por pessoa.
    const sa: Record<string, number> = {};
    const sp: Record<string, number> = {};
    if (summary) {
      Object.entries(summary.byArea as Record<string, UsageBucket>).forEach(([k, v]) => { sa[k] = v.costUsd; });
      Object.entries(summary.byPerson as Record<string, UsageBucket>).forEach(([k, v]) => { sp[k] = v.costUsd; });
    }
    setAreas(a);
    setPeople(p);
    setSpentByArea(sa);
    setSpentByPerson(sp);
    setLoading(false);
  }

  useEffect(() => {
    load();
    ensureModels();
  }, [ensureModels]);

  const allCatalogIds = useMemo(() => catalog.map((m) => m.id), [catalog]);

  // Ao abrir o form de ÁREA: carrega a allowlist atual (ou todos, se nova).
  useEffect(() => {
    if (!areaForm) return;
    const isExisting = areas.some((a) => a.id === areaForm.id);
    if (isExisting) {
      modelsService.getAreaModels(areaForm.id).then(setAreaModelIds).catch(() => setAreaModelIds([]));
    } else {
      setAreaModelIds(allCatalogIds); // nova área já nasce com todos liberados
    }
  }, [areaForm, areas, allCatalogIds]);

  // Ao abrir o form de PESSOA: universo = allowlist da área; seleção = allowlist da pessoa.
  useEffect(() => {
    if (!personForm) return;
    modelsService.getAreaModels(personForm.areaId).then(setPersonAvailableIds).catch(() => setPersonAvailableIds([]));
    const isExisting = people.some((p) => p.id === personForm.id);
    if (isExisting) {
      modelsService.getPersonModels(personForm.id).then(setPersonModelIds).catch(() => setPersonModelIds([]));
    } else {
      setPersonModelIds([]); // nova pessoa começa sem modelos (admin escolhe)
    }
  }, [personForm, people]);

  const peopleByArea = useMemo(() => {
    const map: Record<string, Person[]> = {};
    people.forEach((p) => {
      (map[p.areaId] ??= []).push(p);
    });
    return map;
  }, [people]);

  const totals = useMemo(() => {
    const totalBudget = areas.reduce((acc, a) => acc + a.budgetBRL, 0);
    const totalAllocated = people.reduce((acc, p) => acc + p.budgetBRL, 0);
    return { totalBudget, totalAllocated };
  }, [areas, people]);

  async function saveArea(area: Area) {
    try {
      await budgetService.saveArea(area);
      // Persiste a allowlist de modelos da área (também poda pessoas fora do novo conjunto).
      await modelsService.setAreaModels(area.id, areaModelIds);
      setAreaForm(null);
      toast.success('Área salva.');
      await load();
      reloadAllowed();
    } catch (e) {
      toast.error(e instanceof BudgetError ? e.message : 'Erro ao salvar área.');
    }
  }

  async function savePerson(person: Person) {
    try {
      await budgetService.savePerson(person);
      // Persiste a allowlist da pessoa (backend filtra para ⊆ área).
      await modelsService.setPersonModels(person.id, personModelIds);
      setPersonForm(null);
      toast.success('Pessoa salva.');
      await load();
      reloadAllowed();
    } catch (e) {
      toast.error(e instanceof BudgetError ? e.message : 'Erro ao salvar pessoa.');
    }
  }

  async function confirmDeleteArea() {
    if (!deleteArea) return;
    try {
      await budgetService.removeArea(deleteArea.id);
      toast.success('Área excluída.');
      load();
    } catch (e) {
      toast.error(e instanceof BudgetError ? e.message : 'Erro ao excluir área.');
    }
  }

  async function confirmDeletePerson() {
    if (!deletePerson) return;
    await budgetService.removePerson(deletePerson.id);
    toast.success('Pessoa removida.');
    load();
  }

  return (
    <>
      <PageHeader
        title="Áreas & Budget"
        subtitle={`Budget total ${formatBRL(totals.totalBudget)} · alocado ${formatBRL(totals.totalAllocated)} · gasto no mês ${formatBRL(Object.values(spentByArea).reduce((a, b) => a + b, 0))}`}
        actions={canManage ? (
          <Button
            variant="primary"
            icon={Plus}
            onClick={() =>
              setAreaForm({ id: uid('area'), name: '', budgetBRL: 0, createdAt: nowIso(), updatedAt: nowIso() })
            }
          >
            Nova área
          </Button>
        ) : undefined}
      />

      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {loading ? (
          <CardSkeletonGrid count={3} />
        ) : areas.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title="Nenhuma área ainda"
            description="Crie áreas, defina o budget de cada uma e distribua entre as pessoas."
            action={canManage ? <Button variant="primary" icon={Plus} onClick={() => setAreaForm({ id: uid('area'), name: '', budgetBRL: 0, createdAt: nowIso(), updatedAt: nowIso() })}>Nova área</Button> : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {areas.map((area) => {
              const members = peopleByArea[area.id] ?? [];
              const usage = budgetService.areaUsage(area, people);
              return (
                <Card key={area.id} className="flex flex-col p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/10 text-navy">
                        <Building2 className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-ink">{area.name}</h3>
                        <p className="text-xs text-slate">{members.length} pessoa(s)</p>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        <button onClick={() => setAreaForm(area)} className="rounded-lg p-1.5 text-slate hover:bg-cloud hover:text-ink" aria-label="Editar área"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setDeleteArea(area)} className="rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Excluir área"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 space-y-3">
                    <BudgetBar used={usage.allocated} total={usage.budget} label="Alocado às pessoas" />
                    <BudgetBar used={spentByArea[area.id] ?? 0} total={usage.budget} label="Gasto no mês (real)" />
                  </div>

                  <div className="mt-4 space-y-2">
                    {members.length === 0 ? (
                      <p className="rounded-lg bg-cloud px-3 py-2 text-xs text-slate">Nenhuma pessoa nesta área.</p>
                    ) : (
                      members.map((p) => {
                        return (
                          <div key={p.id} className="rounded-xl border border-line p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-ink">{p.name}</p>
                                <p className="truncate text-xs text-slate">{p.role || p.email}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge tone="neutral">{formatBRL(p.budgetBRL)}</Badge>
                                {canManage && <>
                                  <button onClick={() => setPersonForm(p)} className="rounded-lg p-1 text-slate hover:bg-cloud hover:text-ink" aria-label="Editar pessoa"><Pencil className="h-3.5 w-3.5" /></button>
                                  <button onClick={() => setDeletePerson(p)} className="rounded-lg p-1 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Remover pessoa"><Trash2 className="h-3.5 w-3.5" /></button>
                                </>}
                              </div>
                            </div>
                            <div className="mt-2">
                              <BudgetBar used={spentByPerson[p.id] ?? 0} total={p.budgetBRL} label="Gasto no mês (real)" />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {canManage && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={UserPlus}
                      className="mt-4 self-start"
                      onClick={() =>
                        setPersonForm({ id: uid('per'), name: '', email: '', role: '', areaId: area.id, budgetBRL: 0, createdAt: nowIso(), updatedAt: nowIso() })
                      }
                    >
                      Adicionar pessoa
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Form de área */}
      <Modal
        open={!!areaForm}
        onClose={() => setAreaForm(null)}
        title={areaForm && areas.some((a) => a.id === areaForm.id) ? 'Editar área' : 'Nova área'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAreaForm(null)}>Cancelar</Button>
            <Button variant="primary" onClick={() => areaForm && saveArea(areaForm)} disabled={!areaForm?.name}>Salvar</Button>
          </>
        }
      >
        {areaForm && (
          <>
            <Field label="Nome da área">
              <Input value={areaForm.name} onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })} placeholder="ex.: Atendimento" />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Budget total mensal (US$)" className="mb-0">
                <Input type="number" min={0} step="10" value={areaForm.budgetBRL} onChange={(e) => setAreaForm({ ...areaForm, budgetBRL: Math.max(0, Number(e.target.value)) })} />
              </Field>
              <Field label="Admin da área" hint="aprova pedidos de saldo extra" className="mb-0">
                <Select value={areaForm.adminPersonId ?? ''} onChange={(e) => setAreaForm({ ...areaForm, adminPersonId: e.target.value || undefined })}>
                  <option value="">— ninguém —</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </Field>
            </div>
            <ModelAllowlist
              label="Modelos liberados para a área"
              hint="define o universo disponível para as pessoas desta área"
              models={catalog}
              selected={areaModelIds}
              onChange={setAreaModelIds}
            />
          </>
        )}
      </Modal>

      {/* Form de pessoa */}
      <Modal
        open={!!personForm}
        onClose={() => setPersonForm(null)}
        title={personForm && people.some((p) => p.id === personForm.id) ? 'Editar pessoa' : 'Nova pessoa'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPersonForm(null)}>Cancelar</Button>
            <Button variant="primary" onClick={() => personForm && savePerson(personForm)} disabled={!personForm?.name}>Salvar</Button>
          </>
        }
      >
        {personForm && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nome" className="mb-0">
                <Input value={personForm.name} onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })} />
              </Field>
              <Field label="Área" className="mb-0">
                <Select value={personForm.areaId} onChange={(e) => setPersonForm({ ...personForm, areaId: e.target.value })}>
                  {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="E-mail" className="mt-4">
              <Input type="email" value={personForm.email} onChange={(e) => setPersonForm({ ...personForm, email: e.target.value })} />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Cargo" className="mb-0">
                <Input value={personForm.role ?? ''} onChange={(e) => setPersonForm({ ...personForm, role: e.target.value })} />
              </Field>
              <Field label="Budget mensal (US$)" hint="renova todo mês" className="mb-0">
                <Input type="number" min={0} step="1" value={personForm.budgetBRL} onChange={(e) => setPersonForm({ ...personForm, budgetBRL: Math.max(0, Number(e.target.value)) })} />
              </Field>
            </div>
            <Field label="Saldo extra (US$)" hint="único, não reseta no mês" className="mt-4 mb-0">
              <Input type="number" min={0} step="1" value={personForm.topupBalance ?? 0} onChange={(e) => setPersonForm({ ...personForm, topupBalance: Math.max(0, Number(e.target.value)) })} />
            </Field>
            <AreaHint area={areas.find((a) => a.id === personForm.areaId)} people={people} editingId={personForm.id} />
            <ModelAllowlist
              label="Modelos liberados para a pessoa"
              hint="limitado aos modelos liberados na área (Pessoa ⊆ Área)"
              models={catalog.filter((m) => personAvailableIds.includes(m.id))}
              selected={personModelIds}
              onChange={setPersonModelIds}
              emptyHint="A área desta pessoa não tem modelos liberados. Libere modelos na área primeiro."
            />
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteArea}
        onClose={() => setDeleteArea(null)}
        onConfirm={confirmDeleteArea}
        title="Excluir área"
        message={`Excluir a área "${deleteArea?.name}"? (precisa estar sem pessoas)`}
      />
      <ConfirmDialog
        open={!!deletePerson}
        onClose={() => setDeletePerson(null)}
        onConfirm={confirmDeletePerson}
        title="Remover pessoa"
        message={`Remover "${deletePerson?.name}"? O budget volta a ficar livre na área.`}
      />
    </>
  );
}

// Seletor de modelos (allowlist) agrupado por provedor. Reaproveitado por área e pessoa.
function ModelAllowlist({
  label,
  hint,
  models,
  selected,
  onChange,
  emptyHint,
}: {
  label: string;
  hint?: string;
  models: Model[];
  selected: string[];
  onChange: (ids: string[]) => void;
  emptyHint?: string;
}) {
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const allIds = models.map((m) => m.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));

  const groups = useMemo(() => {
    const map = new Map<string, Model[]>();
    models.forEach((m) => {
      if (!map.has(m.provider)) map.set(m.provider, []);
      map.get(m.provider)!.push(m);
    });
    return Array.from(map.entries());
  }, [models]);

  return (
    <Field label={label} hint={hint} className="mt-4 mb-0">
      {models.length === 0 ? (
        <p className="rounded-lg bg-cloud px-3 py-2 text-xs text-slate">
          {emptyHint ?? 'Nenhum modelo no catálogo.'}
        </p>
      ) : (
        <div className="rounded-xl border border-line p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-medium text-slate">
              <Cpu className="h-3.5 w-3.5" /> {selected.filter((id) => allIds.includes(id)).length}/{allIds.length} selecionado(s)
            </span>
            <button
              type="button"
              className="text-xs font-medium text-brand-orange hover:underline"
              onClick={() => onChange(allSelected ? selected.filter((id) => !allIds.includes(id)) : Array.from(new Set([...selected, ...allIds])))}
            >
              {allSelected ? 'Limpar todos' : 'Selecionar todos'}
            </button>
          </div>
          <div className="space-y-3 max-h-56 overflow-y-auto scrollbar-thin">
            {groups.map(([provider, ms]) => (
              <div key={provider}>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate/70">{provider}</p>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {ms.map((m) => {
                    const active = selected.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggle(m.id)}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm transition-colors',
                          active ? 'border-brand-orange bg-brand-orange/5 text-ink' : 'border-line bg-white text-slate hover:bg-cloud',
                        )}
                      >
                        <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded border', active ? 'border-brand-orange bg-brand-orange text-white' : 'border-line')}>
                          {active && <span className="text-[10px] leading-none">✓</span>}
                        </span>
                        <span className="truncate">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Field>
  );
}

// Mostra o quanto resta livre na área ao editar uma pessoa.
function AreaHint({ area, people, editingId }: { area?: Area; people: Person[]; editingId: string }) {
  if (!area) return null;
  const others = people.filter((p) => p.areaId === area.id && p.id !== editingId).reduce((a, p) => a + p.budgetBRL, 0);
  const free = area.budgetBRL - others;
  return (
    <div className="mt-2 flex items-center gap-2 rounded-lg bg-cloud px-3 py-2 text-xs text-slate">
      <Users className="h-3.5 w-3.5" />
      Livre na área <strong className="text-ink">{area.name}</strong>: {formatBRL(free)} de {formatBRL(area.budgetBRL)}
    </div>
  );
}
